/**
 * CourseAssignmentsLanding — the landing view of /courses/:id/configure.
 *
 * Lists every assignment configuration already created for the course
 * (GET /courses/:id/assignments) with a call-to-action to create a new one.
 *
 * Per-learner rows are grouped back into one card per authored configuration
 * (see assignmentGroups.ts). Editability is per-configuration and keyed to the
 * send state, not the parent course status: a draft (is_notified false) can be
 * edited or deleted via the full wizard; a sent configuration keeps an
 * editable schedule (title/dates via the inline modal — the invitation
 * already went out, so audience/channel/message are locked). "New" and
 * "Edit" hand control back to the parent page, which opens the full wizard
 * at a dedicated route.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import DatePicker from '../../components/common/DatePicker';
import {
  useAssignmentsList,
  useCourse,
  useDeleteAssignment,
  useUpdateAssignment,
  useUpdateCourse,
} from '../../services/courses';
import { groupAssignments, type AssignmentGroup } from './assignmentGroups';
import ManageAudienceModal from './ManageAudienceModal';
import PublishSwapDialog from '../../components/courses/PublishSwapDialog';
import PencilIcon from '@/components/icons/PencilIcon';
import VerifiedIcon from '@/components/icons/VerifiedIcon';

interface Props {
  courseId: string;
  courseTitle: string;
  /** Open the create wizard. */
  onCreateNew: () => void;
  /** Open the edit wizard for a draft configuration (passes one row id). */
  onEdit: (assignmentId: string) => void;
  onBack: () => void;
}

/** Identical to the wizard toolbar's spinner — these two rows of controls
 *  should be indistinguishable (Aleix, 2026-09-10). */
const SpinnerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    style={{ width: 14, height: 14 }}
    className="animate-spin"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
    <path d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

/** A configuration's display name: its title, or a dated default. */
function groupName(g: AssignmentGroup): string {
  if (g.title) return g.title;
  if (g.start_date && g.end_date) return `${fmtDate(g.start_date)} → ${fmtDate(g.end_date)}`;
  return 'Untitled invitation';
}

export default function CourseAssignmentsLanding({
  courseId,
  courseTitle,
  onCreateNew,
  onEdit,
  onBack,
}: Props) {
  const { data: assignments = [], isLoading } = useAssignmentsList(courseId);
  const { data: courseData } = useCourse(courseId);
  const deleteAssignment = useDeleteAssignment();
  const updateAssignment = useUpdateAssignment();
  const updateCourse = useUpdateCourse();

  const isPublished = courseData?.status === 'published';

  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [scheduleGroup, setScheduleGroup] = useState<AssignmentGroup | null>(null);
  const [manageGroup, setManageGroup] = useState<AssignmentGroup | null>(null);
  // Set when publishing is refused for hitting the plan's published-course cap.
  const [swapLimitMessage, setSwapLimitMessage] = useState<string | null>(null);

  // Publishing was only possible in the wizard; a trainer who came straight to
  // Configure had to go back for it (Aleix, 2026-09-10). Learners are not
  // required — publishing an empty course is a legitimate step.
  const handlePublish = async () => {
    try {
      await updateCourse.mutateAsync({ courseId, data: { status: 'published' } });
      toast.success('Course published');
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data;
      if (data?.error === 'PLAN_LIMIT_REACHED') {
        setSwapLimitMessage(data.message ?? 'Your plan is at its published-course limit.');
        return;
      }
      toast.error('Could not publish this course. Please try again.');
    }
  };

  // Same confirmation contract as the wizard's Set-to-draft (Aleix approved
  // the wording 9/3, and asked for the button here too — this is where a live
  // course is actually managed).
  const handleSetDraft = async () => {
    const n = courseData?.enrolled_learner_count ?? 0;
    const impact =
      n > 0
        ? `${n} enrolled learner${n === 1 ? '' : 's'} will lose access to it until it is published again (their progress is kept).`
        : 'Learners will not see it until it is published again.';
    if (!window.confirm(`Move “${courseData?.title ?? 'this course'}” back to draft? ${impact}`)) {
      return;
    }
    try {
      await updateCourse.mutateAsync({ courseId, data: { status: 'draft' } });
      toast.success('Course set to draft');
    } catch {
      toast.error('Could not update course status. Please try again.');
    }
  };
  const groups = useMemo(() => groupAssignments(assignments), [assignments]);

  const handleScheduleSave = async (form: ScheduleForm) => {
    if (!scheduleGroup) return;
    try {
      await Promise.all(
        scheduleGroup.rows.map((row) =>
          updateAssignment.mutateAsync({
            courseId,
            assignmentId: row.id,
            data: {
              title: form.title.trim() || null,
              start_date: form.startDate || undefined,
              end_date: form.endDate,
              // Backend expects ISO-8601 datetime for publish_date; the HTML
              // <input type="date"> emits YYYY-MM-DD, so anchor it to UTC midnight
              // (matches the course wizard's assign path).
              publish_date: form.publishDate ? `${form.publishDate}T00:00:00Z` : null,
            },
          })
        )
      );
      toast.success('Schedule updated');
      setScheduleGroup(null);
    } catch {
      toast.error('Failed to update the schedule');
    }
  };

  const handleDelete = async (g: AssignmentGroup) => {
    if (!window.confirm('Delete this invitation configuration?')) return;
    setDeletingKey(g.key);
    try {
      await Promise.all(
        g.rows.map((r) => deleteAssignment.mutateAsync({ courseId, assignmentId: r.id }))
      );
      toast.success('Invitation deleted');
    } catch {
      toast.error('Failed to delete invitation');
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Toaster position="top-right" />

      {/* Header strip */}
      <div className="max-w-[1080px] mx-auto px-6 pt-6 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-secondary hover:text-text-primary hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,color,transform] duration-150"
        >
          ← Back to Courses
        </button>
        <p className="text-sm text-text-secondary truncate max-w-[60ch]">
          Configuring <span className="font-bold text-text-primary">{courseTitle}</span>
        </p>
      </div>

      <div className="max-w-[1080px] mx-auto px-6 pt-8 pb-24">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-text-primary">Invitations</h1>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 mt-1">
              <Link
                to="/dashboard"
                className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
              >
                Dashboard
              </Link>
              <span className="body-small text-text-secondary">•</span>
              <Link
                to="/courses"
                className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
              >
                Course Library
              </Link>
              <span className="body-small text-text-secondary">•</span>
              <span className="body-small text-text-secondary truncate max-w-[240px]">{courseTitle}</span>
              <span className="body-small text-text-secondary">•</span>
              <span className="body-small text-primary-600 dark:text-primary-350 font-medium">Invitations</span>
            </nav>
            <p className="text-sm text-text-secondary mt-1">
              Who receives this course, and when. Drafts can be edited until they're sent.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Reverse of the wizard's "Configure" jump (Aleix, 9/3) */}
            <Link
              to={`/courses/${courseId}/edit`}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
            >
              Edit course
            </Link>
            {!isPublished && (
              <button
                type="button"
                onClick={handlePublish}
                disabled={updateCourse.isPending}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white bg-success-500 hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {updateCourse.isPending ? (
                  <SpinnerIcon />
                ) : (
                  <VerifiedIcon className="w-4 h-4 [&_path]:fill-current" />
                )}
                Publish
              </button>
            )}
            {isPublished && (
              <button
                type="button"
                onClick={handleSetDraft}
                disabled={updateCourse.isPending}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-grey-900 border border-grey-900/30 dark:text-text-primary dark:border-text-primary/30 hover:bg-grey-900/[0.06] dark:hover:bg-text-primary/[0.08] active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {updateCourse.isPending ? (
                  <SpinnerIcon />
                ) : (
                  <PencilIcon className="w-4 h-4" color="currentColor" accentColor="currentColor" />
                )}
                Set to draft
              </button>
            )}
            {groups.length > 0 && (
              <button
                type="button"
                onClick={onCreateNew}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold active:scale-[0.97] transition-[background-color,transform] duration-150"
              >
                + New invitation
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-20 rounded-2xl bg-text-tertiary/10 animate-pulse" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-16 rounded-2xl border border-dashed border-border-primary/40">
            <div className="w-16 h-16 rounded-full bg-primary-500/10 flex items-center justify-center mb-4">
              <UserGroupIcon className="w-8 h-8 text-primary-500" />
            </div>
            <h2 className="text-lg font-bold text-text-primary mb-1">No Invitations Yet</h2>
            <p className="text-sm text-text-secondary mb-6 max-w-[44ch]">
              Assign learners or groups to this course and choose when they get access.
            </p>
            <button
              type="button"
              onClick={onCreateNew}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold active:scale-[0.97] transition-[background-color,transform] duration-150"
            >
              + Assign learners
            </button>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {groups.map((g) => {
              const editable = !g.is_notified;
              const learners = g.rows.length;
              return (
                <li
                  key={g.key}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl bg-surface-elevated border border-border-primary/15 px-5 py-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-base font-bold text-text-primary truncate">
                        {groupName(g)}
                      </p>
                      <StatusBadge notified={g.is_notified} />
                    </div>
                    <p className="text-xs text-text-secondary mt-1">
                      {learners} learner{learners === 1 ? '' : 's'} · {fmtDate(g.start_date)} →{' '}
                      {fmtDate(g.end_date)}
                      {g.channel ? ` · ${g.channel}` : ''}
                    </p>
                  </div>
                  {editable ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => onEdit(g.rows[0].id)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(g)}
                        disabled={deletingKey === g.key}
                        aria-label="Delete invitation"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-text-disabled hover:text-error-500 hover:bg-error-500/10 active:scale-[0.92] disabled:opacity-40 transition-[background-color,color,transform] duration-150"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Sent groups get direct, named actions instead of a
                          wizard round-trip (Aleix, 8/31): who's invited, and
                          when it runs. Channel & message stay locked — the
                          invitation already went out. */}
                      <button
                        type="button"
                        onClick={() => setManageGroup(g)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
                      >
                        Manage learners
                      </button>
                      <button
                        type="button"
                        onClick={() => setScheduleGroup(g)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
                      >
                        Edit schedule
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {scheduleGroup && (
        <ScheduleEditModal
          group={scheduleGroup}
          saving={updateAssignment.isPending}
          onSave={handleScheduleSave}
          onClose={() => setScheduleGroup(null)}
        />
      )}

      {swapLimitMessage !== null && (
        <PublishSwapDialog
          courseId={courseId}
          courseTitle={courseTitle}
          limitMessage={swapLimitMessage}
          onClose={() => setSwapLimitMessage(null)}
        />
      )}

      {manageGroup && (
        <ManageAudienceModal
          courseId={courseId}
          group={manageGroup}
          onClose={() => setManageGroup(null)}
        />
      )}
    </div>
  );
}

interface ScheduleForm {
  title: string;
  startDate: string;
  endDate: string;
  publishDate: string;
}

/**
 * Schedule editor for a SENT configuration: the invitation already went out,
 * so only the title and dates are editable (one PUT per learner row).
 */
function ScheduleEditModal({
  group,
  saving,
  onSave,
  onClose,
}: {
  group: AssignmentGroup;
  saving: boolean;
  onSave: (form: ScheduleForm) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<ScheduleForm>({
    title: group.title ?? '',
    startDate: group.start_date ?? '',
    endDate: group.end_date ?? '',
    publishDate: group.publish_date ?? '',
  });
  const patch = (p: Partial<ScheduleForm>) => setForm((prev) => ({ ...prev, ...p }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Edit schedule"
      data-testid="edit-schedule-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-bold text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(form)}
            disabled={saving || !form.endDate}
            className="px-5 py-2 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold disabled:opacity-50 transition-colors"
            data-testid="save-schedule-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">
          This assignment was already sent, so its audience and invitation are
          locked — but the schedule stays yours to adjust.
        </p>
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5" htmlFor="schedule-title">
            Title
          </label>
          <input
            id="schedule-title"
            type="text"
            value={form.title}
            onChange={(e) => patch({ title: e.target.value })}
            className="input-primary w-full"
            placeholder="Invitation title"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DatePicker
            id="schedule-start"
            label="Start date"
            value={form.startDate}
            max={form.endDate || undefined}
            onChange={(v) => patch({ startDate: v })}
          />
          <DatePicker
            id="schedule-end"
            label="End date"
            value={form.endDate}
            min={form.startDate || undefined}
            onChange={(v) => patch({ endDate: v })}
          />
        </div>
        <DatePicker
          id="schedule-publish"
          label="Publish date (optional)"
          value={form.publishDate}
          onChange={(v) => patch({ publishDate: v })}
        />
        <p className="text-xs text-text-secondary">
          The end date is for visibility and reporting only — learners can
          still complete the course after it passes (e.g. after a leave).
        </p>
      </div>
    </Modal>
  );
}

function StatusBadge({ notified }: { notified: boolean }) {
  return notified ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-success-500/15 text-success-600">
      Sent
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-text-tertiary/15 text-text-secondary">
      Draft
    </span>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function UserGroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-2a3 3 0 10-2.5-1.34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
